import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShoppingCart, Eye, CheckCircle, XCircle, DollarSign, Search,
  Mail, Phone, Calendar, Package, FileText, Truck, MessageSquare, Copy,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_approval: { label: 'Pending Approval', cls: 'bg-orange-100 text-orange-800' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800' },
  shipped: { label: 'Shipped', cls: 'bg-blue-100 text-blue-800' },
  delivered: { label: 'Delivered', cls: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-700' },
};

const PAID_BADGE = {
  unpaid: { label: 'Unpaid', cls: 'bg-rose-100 text-rose-800' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-800' },
};

const fmtTsh = (n) => `Tsh ${Number(n || 0).toLocaleString()}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');

export default function CustomerOrders({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', note: '' });
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsData, setSmsData] = useState({ text: '', phone: '' });

  const headers = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customer-orders`, { headers: headers() });
      const list = (res.data || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(list);
    } catch (e) {
      toast.error('Failed to load customer orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filtered = orders.filter((o) => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        o.order_number?.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_email?.toLowerCase().includes(q) ||
        o.customer_phone?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = {
    all: orders.length,
    pending_approval: orders.filter((o) => o.status === 'pending_approval').length,
    approved: orders.filter((o) => o.status === 'approved').length,
    rejected: orders.filter((o) => o.status === 'rejected').length,
    shipped: orders.filter((o) => o.status === 'shipped').length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
  };

  const onView = (o) => {
    setSelected(o);
    setViewOpen(true);
  };

  const onApprove = async (o) => {
    setBusyId(o.id);
    try {
      await axios.put(`${API}/admin/customer-orders/${o.id}/approve`, {}, { headers: headers() });
      toast.success(`Order ${o.order_number} approved`);
      await fetchOrders();
      if (selected?.id === o.id) setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (o) => {
    setSelected(o);
    setRejectReason('');
    setRejectOpen(true);
  };

  const submitReject = async () => {
    if (!selected) return;
    setBusyId(selected.id);
    try {
      await axios.put(
        `${API}/admin/customer-orders/${selected.id}/reject`,
        { reason: rejectReason },
        { headers: headers() }
      );
      toast.success(`Order ${selected.order_number} rejected`);
      setRejectOpen(false);
      await fetchOrders();
      setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const onMarkPaid = async (o) => {
    setBusyId(o.id);
    try {
      await axios.put(`${API}/admin/customer-orders/${o.id}/mark-paid`, {}, { headers: headers() });
      toast.success(`Order ${o.order_number} marked as paid`);
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setBusyId(null);
    }
  };

  const convertToInvoice = async (o) => {
    setBusyId(o.id);
    try {
      const res = await axios.post(`${API}/admin/customer-orders/${o.id}/convert-to-invoice`, {}, { headers: headers() });
      toast.success(`Invoice ${res.data.invoice.invoice_number} created`);
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setBusyId(null);
    }
  };

  const convertToDeliveryNote = async (o) => {
    setBusyId(o.id);
    try {
      const res = await axios.post(`${API}/admin/customer-orders/${o.id}/convert-to-delivery-note`, {}, { headers: headers() });
      toast.success(`Delivery note ${res.data.delivery_note.delivery_number} created`);
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create delivery note');
    } finally {
      setBusyId(null);
    }
  };

  const openPayment = (o) => {
    setSelected(o);
    const balance = Math.max(0, (o.total_amount || 0) - (o.total_paid || 0));
    setPaymentForm({ amount: balance > 0 ? String(balance) : '', method: 'cash', note: '' });
    setPaymentOpen(true);
  };

  const submitPayment = async () => {
    if (!selected) return;
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setBusyId(selected.id);
    try {
      const res = await axios.post(
        `${API}/admin/customer-orders/${selected.id}/payments`,
        { amount: amt, method: paymentForm.method, note: paymentForm.note },
        { headers: headers() }
      );
      toast.success('Payment recorded');
      setPaymentOpen(false);
      // Auto-open SMS dialog if there's still a balance OR even when fully paid (confirmation)
      setSmsData({ text: res.data.sms_text, phone: res.data.customer_phone });
      setSmsOpen(true);
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setBusyId(null);
    }
  };

  const openReminder = async (o) => {
    setSelected(o);
    try {
      const res = await axios.get(`${API}/admin/customer-orders/${o.id}/sms-reminder`, { headers: headers() });
      setSmsData({ text: res.data.sms_text, phone: res.data.customer_phone });
      setSmsOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build reminder');
    }
  };

  const copySms = async () => {
    try {
      await navigator.clipboard.writeText(smsData.text);
      toast.success('SMS text copied to clipboard');
    } catch (_) {
      toast.error('Could not access clipboard — long-press the text to copy manually');
    }
  };

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(smsData.phone || '');
      toast.success('Phone number copied');
    } catch (_) {
      toast.error('Could not access clipboard');
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ShoppingCart className="text-[#EA5829]" size={28} />
            Customer Orders
          </h1>
          <p className="text-slate-600 mt-1">
            Orders placed directly by registered customers via the e-commerce portal
          </p>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search order #, customer, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-72"
            data-testid="customer-orders-search"
          />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending_approval" className="text-orange-700">
            Pending ({counts.pending_approval})
          </TabsTrigger>
          <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="shipped">Shipped ({counts.shipped})</TabsTrigger>
          <TabsTrigger value="delivered">Delivered ({counts.delivered})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <Package size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-semibold">No customer orders yet</p>
              <p className="text-sm">When a customer places an order from the portal, it will appear here.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Placed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => {
                  const sb = STATUS_BADGE[o.status] || { label: o.status, cls: 'bg-slate-100 text-slate-700' };
                  const pb = PAID_BADGE[o.paid_status] || { label: o.paid_status || '-', cls: 'bg-slate-100 text-slate-700' };
                  return (
                    <TableRow key={o.id} data-testid={`customer-order-row-${o.id}`}>
                      <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                      <TableCell>
                        <div className="font-semibold text-sm">{o.customer_name}</div>
                        <div className="text-xs text-slate-500">{o.customer_phone || o.customer_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{o.customer_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{(o.items || []).length}</TableCell>
                      <TableCell className="font-semibold">{fmtTsh(o.total_amount)}</TableCell>
                      <TableCell>
                        <Badge className={sb.cls}>{sb.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={pb.cls}>{pb.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{fmtDate(o.created_at)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(o)}
                          data-testid={`view-customer-order-${o.id}`}
                          title="View details"
                        >
                          <Eye size={14} />
                        </Button>
                        {o.status === 'pending_approval' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onApprove(o)}
                              disabled={busyId === o.id}
                              className="text-green-700"
                              data-testid={`approve-customer-order-${o.id}`}
                              title="Approve"
                            >
                              <CheckCircle size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReject(o)}
                              disabled={busyId === o.id}
                              className="text-rose-700"
                              data-testid={`reject-customer-order-${o.id}`}
                              title="Reject"
                            >
                              <XCircle size={14} />
                            </Button>
                          </>
                        )}
                        {['approved', 'shipped', 'delivered', 'completed'].includes(o.status) && (
                          <>
                            {!o.invoice_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => convertToInvoice(o)}
                                disabled={busyId === o.id}
                                className="text-purple-700"
                                data-testid={`convert-invoice-${o.id}`}
                                title="Convert to Invoice"
                              >
                                <FileText size={14} />
                              </Button>
                            )}
                            {!o.delivery_note_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => convertToDeliveryNote(o)}
                                disabled={busyId === o.id}
                                className="text-cyan-700"
                                data-testid={`convert-delivery-${o.id}`}
                                title="Convert to Delivery Note"
                              >
                                <Truck size={14} />
                              </Button>
                            )}
                            {o.paid_status !== 'paid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPayment(o)}
                                disabled={busyId === o.id}
                                className="text-emerald-700"
                                data-testid={`add-payment-${o.id}`}
                                title="Record payment"
                              >
                                <DollarSign size={14} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReminder(o)}
                              className="text-orange-700"
                              data-testid={`sms-reminder-${o.id}`}
                              title="SMS reminder"
                            >
                              <MessageSquare size={14} />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-customer-order-dialog">
          <DialogHeader>
            <DialogTitle>
              Order {selected?.order_number}
              {selected && (
                <Badge className={`ml-2 ${(STATUS_BADGE[selected.status] || {}).cls}`}>
                  {(STATUS_BADGE[selected.status] || {}).label || selected.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Customer</div>
                  <div className="font-semibold">{selected.customer_name}</div>
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                    <Mail size={11} /> {selected.customer_email}
                  </div>
                  <div className="text-xs text-slate-600 flex items-center gap-1">
                    <Phone size={11} /> {selected.customer_phone}
                  </div>
                  <Badge variant="outline" className="mt-2 capitalize">{selected.customer_type}</Badge>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Order</div>
                  <div className="text-xs flex items-center gap-1 text-slate-600">
                    <Calendar size={11} /> Placed: {fmtDate(selected.created_at)}
                  </div>
                  {selected.approved_at && (
                    <div className="text-xs flex items-center gap-1 text-slate-600">
                      <CheckCircle size={11} /> Approved: {fmtDate(selected.approved_at)}
                    </div>
                  )}
                  <div className="text-xs text-slate-600">
                    Payment: <Badge className={(PAID_BADGE[selected.paid_status] || {}).cls}>
                      {(PAID_BADGE[selected.paid_status] || {}).label || selected.paid_status}
                    </Badge>
                  </div>
                  {selected.payment_method && (
                    <div className="text-xs text-slate-600 mt-1">Method: {selected.payment_method}</div>
                  )}
                </div>
              </div>

              {selected.delivery_address && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Delivery Address</div>
                  <div className="text-sm">{selected.delivery_address}</div>
                </div>
              )}

              <div>
                <div className="text-sm font-semibold mb-2">Items</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selected.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.product_name}</TableCell>
                        <TableCell className="text-right">{it.quantity}</TableCell>
                        <TableCell className="text-right">{fmtTsh(it.unit_price)}</TableCell>
                        <TableCell className="text-right font-semibold">{fmtTsh(it.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end mt-3 gap-6 text-sm">
                  <div>
                    <span className="text-slate-500">Subtotal: </span>
                    <span className="font-semibold">{fmtTsh(selected.subtotal)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Total: </span>
                    <span className="text-lg font-bold text-[#EA5829]">{fmtTsh(selected.total_amount)}</span>
                  </div>
                </div>
              </div>

              {selected.rejection_reason && (
                <div className="bg-rose-50 border border-rose-200 p-3 rounded-lg">
                  <div className="text-xs text-rose-700 font-semibold">Rejection Reason</div>
                  <div className="text-sm text-rose-800 mt-1">{selected.rejection_reason}</div>
                </div>
              )}

              {/* Linked documents */}
              {(selected.invoice_number || selected.delivery_number || selected.payment_reference) && (
                <div className="flex flex-wrap gap-2">
                  {selected.payment_reference && (
                    <Badge className="bg-amber-100 text-amber-900">
                      Payment Ref: <span className="font-mono ml-1">{selected.payment_reference}</span>
                    </Badge>
                  )}
                  {selected.invoice_number && (
                    <Badge className="bg-purple-100 text-purple-800">
                      <FileText size={11} className="mr-1" /> {selected.invoice_number}
                    </Badge>
                  )}
                  {selected.delivery_number && (
                    <Badge className="bg-cyan-100 text-cyan-800">
                      <Truck size={11} className="mr-1" /> {selected.delivery_number}
                    </Badge>
                  )}
                </div>
              )}

              {/* Payment history */}
              {(selected.payments && selected.payments.length > 0) || selected.total_paid > 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-emerald-900">Payments</div>
                    <div className="text-xs text-emerald-800">
                      Paid: <b>{fmtTsh(selected.total_paid)}</b>
                      {' · '}
                      Balance: <b className={selected.balance_due > 0 ? 'text-rose-700' : 'text-emerald-800'}>
                        {fmtTsh(selected.balance_due ?? Math.max(0, selected.total_amount - (selected.total_paid || 0)))}
                      </b>
                    </div>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {(selected.payments || []).map((p) => (
                      <div key={p.id} className="text-xs flex justify-between bg-white rounded px-2 py-1">
                        <span>
                          <b>{fmtTsh(p.amount)}</b> · {p.method}
                          {p.note ? ` · ${p.note}` : ''}
                        </span>
                        <span className="text-slate-500">
                          {fmtDate(p.recorded_at)}
                          {p.recorded_by_name ? ` · ${p.recorded_by_name}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selected.status === 'pending_approval' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => onApprove(selected)}
                    disabled={busyId === selected.id}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="dialog-approve-btn"
                  >
                    <CheckCircle size={14} className="mr-2" /> Approve Order
                  </Button>
                  <Button
                    onClick={() => openReject(selected)}
                    disabled={busyId === selected.id}
                    variant="outline"
                    className="flex-1 text-rose-700 border-rose-300 hover:bg-rose-50"
                    data-testid="dialog-reject-btn"
                  >
                    <XCircle size={14} className="mr-2" /> Reject Order
                  </Button>
                </div>
              )}

              {['approved', 'shipped', 'delivered', 'completed'].includes(selected.status) && (
                <div className="flex gap-2 pt-2 flex-wrap">
                  {!selected.invoice_id && (
                    <Button
                      onClick={() => convertToInvoice(selected)}
                      disabled={busyId === selected.id}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 min-w-[140px]"
                      data-testid="dialog-convert-invoice"
                    >
                      <FileText size={14} className="mr-2" /> Create Invoice
                    </Button>
                  )}
                  {!selected.delivery_note_id && (
                    <Button
                      onClick={() => convertToDeliveryNote(selected)}
                      disabled={busyId === selected.id}
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 min-w-[160px]"
                      data-testid="dialog-convert-delivery"
                    >
                      <Truck size={14} className="mr-2" /> Create Delivery Note
                    </Button>
                  )}
                  {selected.paid_status !== 'paid' && (
                    <Button
                      onClick={() => openPayment(selected)}
                      disabled={busyId === selected.id}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 min-w-[140px]"
                      data-testid="dialog-add-payment"
                    >
                      <DollarSign size={14} className="mr-2" /> Record Payment
                    </Button>
                  )}
                  <Button
                    onClick={() => openReminder(selected)}
                    variant="outline"
                    className="flex-1 min-w-[140px]"
                    data-testid="dialog-sms-reminder"
                  >
                    <MessageSquare size={14} className="mr-2" /> SMS Reminder
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-customer-order-dialog">
          <DialogHeader>
            <DialogTitle>Reject Order {selected?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              The customer will be notified. Provide a clear reason so they can update or place a new order.
            </p>
            <Input
              placeholder="Reason (e.g. out of stock, payment not received)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button
                onClick={submitReject}
                disabled={busyId === selected?.id}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-reject-btn"
              >
                Reject Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment — {selected?.order_number}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 p-2 rounded">
                <div>Total: <b>{fmtTsh(selected.total_amount)}</b></div>
                <div>Paid: <b className="text-emerald-700">{fmtTsh(selected.total_paid)}</b></div>
                <div>
                  Balance:{' '}
                  <b className="text-rose-700">
                    {fmtTsh(Math.max(0, selected.total_amount - (selected.total_paid || 0)))}
                  </b>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Amount (Tsh) *</label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="e.g. 200000"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Method</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  data-testid="payment-method-select"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money (M-Pesa / Airtel / Tigo)</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Note (optional)</label>
                <Input
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                  placeholder="e.g. ref #ABC123, first installment"
                  data-testid="payment-note-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
                <Button
                  onClick={submitPayment}
                  disabled={busyId === selected.id}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-payment-btn"
                >
                  Record Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SMS Reminder dialog */}
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent data-testid="sms-reminder-dialog">
          <DialogHeader>
            <DialogTitle>Send SMS Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="text-xs text-slate-600 mb-1">Customer phone</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border rounded px-2 py-1 text-sm font-mono">
                  {smsData.phone || '—'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyPhone}
                  data-testid="copy-phone-btn"
                >
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-600 mb-1">Message text</div>
              <textarea
                readOnly
                value={smsData.text}
                className="w-full border rounded-md px-3 py-2 text-sm h-32 resize-none bg-white"
                data-testid="sms-text-area"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 p-3 rounded text-xs text-amber-900">
              <b>Manual SMS mode:</b> copy the text and phone number, then paste them into your phone's
              SMS / WhatsApp app. To enable automatic sending, set up Africa's Talking or Vodacom Bulk SMS
              and your developer can wire it up in 5 minutes.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSmsOpen(false)}>Close</Button>
              <Button
                onClick={copySms}
                className="bg-[#EA5829] hover:bg-[#d24a20]"
                data-testid="copy-sms-btn"
              >
                <Copy size={14} className="mr-2" /> Copy SMS Text
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
